import { Form, Formik } from 'formik';
import { Modal, Row, FormGroup, FormLabel } from 'react-bootstrap';
import DayjsDatePicker from '../../../Admin/components/DayjsDatePicker/DayjsDatePicker';
import { disabledDateWeekend } from '../../../utils/date-utils';
import dayjs from 'dayjs';
import style from './date-modal.module.scss';
import { useCartContext } from '../../../context/CartContext/CartContext';
import { Dispatch, FC, SetStateAction, useState, useEffect } from 'react';
import { ErrorMessageLabel } from '../../../Admin/components/ErrorMessageLabel/ErrorMessageLabel';
import * as Yup from 'yup';
import { Order } from '../../../models/Order/Order';
import { toast } from 'react-toastify';

interface DateModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onAction: () => void;
}

const DataModalValidationSchema = Yup.object().shape({
  start_date: Yup.date().required('Unesite datum pocetka rezervacije'),
  end_date: Yup.date()
    .required('Unesite datum kraja rezervacije')
    .min(
      Yup.ref('start_date'),
      'Datum kraja rezervacije mora biti veci od datuma pocetka rezervacije'
    )
});

const DateModal: FC<DateModalProps> = ({ open = false, setOpen, onAction }) => {
  const { cart, cartSetStartEndDates } = useCartContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Local state for the form inputs
  const [formValues, setFormValues] = useState({
    start_date: '',
    end_date: ''
  });

  // Local state for the dynamic preview calculations
  const [previewPriceFactor, setPreviewPriceFactor] = useState(1.0);
  const [previewNumberOfDays, setPreviewNumberOfDays] = useState(0);

  useEffect(() => {
    if (open && cart) {
      setFormValues({
        start_date: cart.startDate || '',
        end_date: cart.endDate || ''
      });
      setPreviewNumberOfDays(cart.numberOfDays);
      setPreviewPriceFactor(cart.priceFactor);
    }
  }, [open, cart]);

  const calculateDays = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (
      isNaN(startDate.getTime()) ||
      isNaN(endDate.getTime()) ||
      endDate < startDate
    ) {
      return 0;
    }
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleDateChange = async (start: string, end: string) => {
    if (start && end && new Date(end) >= new Date(start)) {
      try {
        const days = calculateDays(start, end);
        setPreviewNumberOfDays(days);

        const response = await Order.calculatePriceFactor(start, end);
        setPreviewPriceFactor(response.price_factor);
      } catch (error) {
        console.error('Error calculating preview:', error);
      }
    }
  };

  const handleSubmit = async (values: { start_date: string; end_date: string }) => {
    try {
      setIsSubmitting(true);
      await cartSetStartEndDates(values.start_date, values.end_date);
      toast.success('Datumi rezervacije su uspešno sačuvani');
      setOpen(false);
      onAction?.();
    } catch (error) {
      console.error(error);
      toast.error('Došlo je do greške prilikom čuvanja datuma.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={open}
      onHide={() => setOpen(false)}
      centered
      backdrop={true}
      className={style.wrapper}
    >
      <Modal.Header closeButton className={style.header}>
        <Modal.Title>Izaberi datum</Modal.Title>
      </Modal.Header>
      <Modal.Body className={style.body}>
        <Formik
          enableReinitialize
          initialValues={formValues}
          validationSchema={DataModalValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, dirty }) => (
            <Form autoComplete="off">
              <Row className="mb-4">
                <FormGroup>
                  <FormLabel className="fw-bold">Početak rezervacije</FormLabel>
                  <DayjsDatePicker
                    name="start_date"
                    inputReadOnly
                    className="w-100"
                    format="YYYY-MM-DD"
                    placeholder="Izaberite datum"
                    value={values.start_date ? dayjs(values.start_date) : null}
                    onChange={(date: any, dateString: any) => {
                      setFieldValue('start_date', dateString);
                      handleDateChange(dateString, values.end_date);
                    }}
                    disabledDate={disabledDateWeekend}
                  />
                  <ErrorMessageLabel name="start_date" />
                </FormGroup>
              </Row>
              <Row className="mb-4">
                <FormGroup>
                  <FormLabel className="fw-bold">Kraj Rezervacije</FormLabel>
                  <DayjsDatePicker
                    name="end_date"
                    inputReadOnly
                    className="w-100"
                    format="YYYY-MM-DD"
                    placeholder="Izaberite datum"
                    value={values.end_date ? dayjs(values.end_date) : null}
                    onChange={(date: any, dateString: any) => {
                      setFieldValue('end_date', dateString);
                      handleDateChange(values.start_date, dateString);
                    }}
                    disabledDate={disabledDateWeekend}
                  />
                  <ErrorMessageLabel name="end_date" />
                </FormGroup>
              </Row>
              {previewNumberOfDays > 0 && (
                <Row className="mb-4">
                  <div className={style.infoSection}>
                    <div className={style.infoRow}>
                      <span>Broj dana:</span>
                      <span>{previewNumberOfDays}</span>
                    </div>
                    <div className={style.infoRow}>
                      <span>Multiplikator cene:</span>
                      <span>{previewPriceFactor.toFixed(2)}x</span>
                    </div>
                  </div>
                </Row>
              )}
              <Row>
                <div>
                  <button
                    type="submit"
                    className={style.button}
                    disabled={isSubmitting || !dirty}
                  >
                    {isSubmitting ? 'Čuvanje...' : 'Izaberi datum'}
                  </button>
                </div>
              </Row>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default DateModal;
